import React, { useState, useEffect } from 'react';
import { HiDotsVertical } from 'react-icons/hi';
import { MdEdit, MdDelete } from 'react-icons/md';
import { ChatItem } from '../types';

interface ChatHistoryItemProps {
  chat: ChatItem;
  isSelected: boolean;
  hasUnread?: boolean;
  onSelect: (chatId: string) => void;
  onRename: (chat: ChatItem) => void;
  onDelete: (chat: ChatItem) => void;
}

const ChatHistoryItem: React.FC<ChatHistoryItemProps> = React.memo(({
  chat,
  isSelected,
  hasUnread = false,
  onSelect,
  onRename,
  onDelete,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const formatDate = (primary?: string, fallback?: string) => {
    const parseDate = (value?: string) => {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const date = parseDate(primary) || parseDate(fallback);
    if (!date) return 'Just now';

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return 'Just now';
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRename(chat);
    setIsMenuOpen(false);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(chat);
    setIsMenuOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setIsMenuOpen(false);
    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMenuOpen]);

  return (
    <div
      onClick={() => onSelect(chat.chat_id)}
      className={`group relative px-3 py-2 rounded-lg cursor-pointer transition-colors ${
        isSelected
          ? 'bg-blue-100 text-blue-900'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate flex items-center gap-2">
            {hasUnread && !isSelected && (
              <span className="inline-block w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
            )}
            <span className="truncate">{chat.title}</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            <span>{formatDate(chat.last_message_at, chat.created_at)}</span>
          </div>
        </div>

        {/* Three-dot menu button - shows on hover */}
        <div className="relative flex-shrink-0">
          <button
            onClick={handleMenuClick}
            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-all"
            title="More options"
          >
            <HiDotsVertical className="w-4 h-4 text-gray-600" />
          </button>

          {/* Dropdown menu */}
          {isMenuOpen && (
            <div className="absolute right-0 top-6 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
              <button
                onClick={handleRenameClick}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <MdEdit className="w-4 h-4" />
                <span>Rename</span>
              </button>
              <button
                onClick={handleDeleteClick}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <MdDelete className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500 mt-1">
        <span>{chat.message_count} msg{chat.message_count !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.chat.chat_id === nextProps.chat.chat_id &&
    prevProps.chat.title === nextProps.chat.title &&
    prevProps.chat.last_message_at === nextProps.chat.last_message_at &&
    prevProps.chat.message_count === nextProps.chat.message_count &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.hasUnread === nextProps.hasUnread
  );
});

export default ChatHistoryItem;
